import { MinMaxFilter } from '@appTypes/FilterTypes';
import { theme } from '@theme';
import { formatDate } from '@utils/DateUtils';
import { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { Resolutions } from '../../../enums/Resolutions';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import useWindowDimensions from '../../../hooks/UseWindowDimensions';
import { Icon } from '../../style/Icon';

type CalendarProps = {
  date?: Date;
  maxDate?: Date;
  minDate?: Date;
  isStartDate: boolean;
  isCalendarOpen: boolean;
  isOtherCalendarOpen: boolean;
  value: MinMaxFilter<number>;
  setIsCalendarOpen?: Dispatch<SetStateAction<boolean>>;
  setIsOtherCalendarOpen?: Dispatch<SetStateAction<boolean>>;
  onChange: (date: Date, isStartDate: boolean, value?: MinMaxFilter<number>) => void;
};

type DatePickerWrapperProps = {
  isMobileDevice?: boolean;
};

const Calendar: FC<CalendarProps> = (props) => {
  const [dateValue, setDateValue] = useState<Date>(props.date);
  const { width } = useWindowDimensions();
  const { isMobileDevice } = useCheckDevice();

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {};

  const handleChange = (date: Date): void => {
    setDateValue(date);
    props.setIsCalendarOpen(false);
    props.onChange(date, props.isStartDate, props.value);
  };

  const handleClick = (e: any) => {
    e.preventDefault();
    props.setIsCalendarOpen(!props.isCalendarOpen);
    if (props.isOtherCalendarOpen) {
      props.setIsOtherCalendarOpen(false);
    }
  };

  return (
    <>
      <Wrapper>
        <div className='icon'>
          <Icon
            data-testid='calender-icon'
            type='calendar'
            size={22}
            onClick={handleClick}
            color={theme.palette.purple}
            marginRight={10}
          />
        </div>
        <input
          type='text'
          className='form-control date'
          pattern='\d{1,2}/\d{1,2}/\d{4}'
          value={dateValue !== null ? formatDate(dateValue) : ''}
          onChange={onInputChange}
          onClick={handleClick}
          readOnly={isMobileDevice}
          data-testid='calender-input'
        />
      </Wrapper>
      {props.isCalendarOpen && Resolutions.Tablet <= width && (
        <DatePickerWrapper isMobileDevice={isMobileDevice}>
          <DatePicker
            selected={dateValue}
            onChange={handleChange}
            maxDate={props.maxDate}
            minDate={props.minDate}
            dropdownMode='select'
            inline
            disabledKeyboardNavigation
            showDisabledMonthNavigation
          />
        </DatePickerWrapper>
      )}
      {props.isCalendarOpen && Resolutions.Tablet > width && (
        <DatePickerWrapperMobile>
          <DatePicker
            selected={dateValue}
            onChange={handleChange}
            maxDate={props.maxDate}
            minDate={props.minDate}
            dropdownMode='select'
            inline
            disabledKeyboardNavigation
            showDisabledMonthNavigation
          />
        </DatePickerWrapperMobile>
      )}
    </>
  );
};

export default Calendar;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  margin-top: 2px;
  border-radius: 4px;
  .date {
    background: ${theme.palette.white};
    width: 90%;
    margin-right: 25px;
  }
  .icon {
    border: 1px solid ${theme.palette.backgroundGray};
    background-color: ${theme.palette.gray};
    margin-left: 20px;
    padding-top: 5px;
    padding-left: 7px;
    border-radius: 4px;
  }
`;

const DatePickerWrapper = styled.div<DatePickerWrapperProps>`
  transform: scale(1.5);
  margin-top: 20%;
  margin-left: 32px;
  position: absolute;
  z-index: 10;

  .react-datepicker {
    font-family: 'proxima-nova-n4', 'proxima-nova', 'Helvetica', 'arial', sans-serif;
    font-size: 9px;
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__month-container {
    width: 155px;
    font-family: 'proxima-nova-n4', 'proxima-nova', 'Helvetica', 'arial', sans-serif;
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header,
  .react-datepicker__day-name,
  .react-datepicker__time-name,
  .react-datepicker__day {
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__day--disabled {
    color: ${theme.palette.lightGrey};
  }

  .react-datepicker__day--selected {
    color: ${theme.palette.text.white};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background-color: ${theme.palette.purple};
  }

  .react-datepicker__day--selected:hover,
  .react-datepicker__day--in-selecting-range:hover,
  .react-datepicker__day--in-range:hover,
  .react-datepicker__month-text--selected:hover,
  .react-datepicker__month-text--in-selecting-range:hover,
  .react-datepicker__month-text--in-range:hover,
  .react-datepicker__quarter-text--selected:hover,
  .react-datepicker__quarter-text--in-selecting-range:hover,
  .react-datepicker__quarter-text--in-range:hover,
  .react-datepicker__year-text--selected:hover,
  .react-datepicker__year-text--in-selecting-range:hover,
  .react-datepicker__year-text--in-range:hover,
  .react-datepicker__day--keyboard-selected:hover,
  .react-datepicker__month-text--keyboard-selected:hover,
  .react-datepicker__quarter-text--keyboard-selected:hover,
  .react-datepicker__year-text--keyboard-selected:hover {
    background-color: ${theme.palette.purple};
  }

  ${(props) =>
    props.isMobileDevice &&
    `
    margin-left: 6rem;
    margin-top: 4.5rem;
  `}
`;

const DatePickerWrapperMobile = styled(DatePickerWrapper)`
  transform: scale(1.7);
  margin-left: 4.5rem;
  margin-top: 4rem;
`;
